import {
  AidaMappedQualification,
  AidaQualifications,
} from './../../../../shared/services/aida/aida-payload/aida-payload.service';
import { AidaQbpSelectionQuota } from './../../../../shared/interfaces/aida.interface';
import { QuotaPayload } from './../../../../shared/interfaces/quota.interface';
import {
  AIDA_ALLOWED_QUALIFICATION_CODES,
  AIDA_ELIGIBILITY_ERROR_MESSAGES,
} from '../../ai-delivery-assistant.const';
import { AidaQuotaCriteriaValidaor } from './quota-criteria-filter';
import { QUALIFICATION_CODES } from '../../../../shared/constants/qualification-code';
import { Constants } from '../../../../shared/constants/create-survey.constants';

interface FilterQuota<T> {
  eligible: T[];
  ineligible: T[];
  partialIneligible: T[];
  errorMessages: string[];
}

type FilterQuotaKey = keyof Omit<FilterQuota<unknown>, 'errorMessages'>;

const QUOTA_VALIDATE_TYPE: Record<string, FilterQuotaKey> = {
  ELIGIBLE: 'eligible',
  INELIGIBLE: 'ineligible',
  PARTIAL_INELIGIBLE: 'partialIneligible',
};
//
export class AidaQuotaFilter {
  private readonly _selectionQuotas: AidaQbpSelectionQuota[] = [];
  private readonly _surveyQuotas: QuotaPayload[] = [];
  private readonly _isSurveyQuotas: boolean = true;
  private readonly _countryCode: string;
  private _aidaAllowedQualificationCodes = new Set(
    AIDA_ALLOWED_QUALIFICATION_CODES
  );

  private _partialInvalidQualificationCodes = new Set<number>(); // if AIDA allowed quotas criteria validation failed, will store those in this
  private _invalidQualificationCodes = new Set<number>();
  private _mappedDBQualificationData: AidaQualifications = {};
  private _errorMessages: string[] = [];

  constructor(
    private readonly _completes: number,
    mappedDBQualificationCodes: AidaQualifications,
    countryCode: string,
    surveyQuotas: QuotaPayload[] = [],
    selectionQuotas: AidaQbpSelectionQuota[] = [],
    isSurveyQuotas: boolean = true
  ) {
    this._mappedDBQualificationData = mappedDBQualificationCodes;
    this._selectionQuotas = selectionQuotas;
    this._surveyQuotas = surveyQuotas;
    this._isSurveyQuotas = isSurveyQuotas;
    this._countryCode = countryCode;
    if (this._countryCode === Constants.AUSTRALIA_SHORT_CODE) {
      this._aidaAllowedQualificationCodes.add(QUALIFICATION_CODES.DIVISION);
    }
  }

  public filter() {
    if (!this._surveyQuotas.length) {
      return {
        eligible: [],
        ineligible: [],
        partialIneligible: [],
        errorMessages: [],
      };
    }
    const _filteredSurveyQuotas = this._filterSurveyQuotas();

    if (this._isSurveyQuotas) {
      return _filteredSurveyQuotas;
    }

    return this._filterSelectionQuotas();
  }

  private _filterSurveyQuotas() {
    this._surveyQuotas.forEach((quota) => this._checkForInvalidQuotas(quota));

    const result = this._surveyQuotas.reduce(
      (acc, quota) => {
        const groupKey = this._filterSurveyQuotaCriteria(quota);
        acc[groupKey].push(quota);
        return acc;
      },
      {
        eligible: [],
        ineligible: [],
        partialIneligible: [],
        errorMessages: [],
      } as FilterQuota<QuotaPayload>
    );

    this._evaluteErrors(result);
    result.errorMessages = this._errorMessages;
    return result;
  }

  private _evaluteErrors(result: FilterQuota<QuotaPayload>) {
    if (
      (!result.eligible.length && !result.partialIneligible.length) ||
      result.ineligible.length
    ) {
      if (this._countryCode === Constants.AUSTRALIA_SHORT_CODE) {
        this._errorMessages = [
          AIDA_ELIGIBILITY_ERROR_MESSAGES.ELIGIBLE_QUOTAS_AU,
        ];
        return;
      }
      this._errorMessages = [AIDA_ELIGIBILITY_ERROR_MESSAGES.ELIGIBLE_QUOTAS];
      return;
    }

    if (result.eligible.length && result.partialIneligible.length) {
      this._errorMessages = [];
      return;
    }

    if (result.partialIneligible.length) {
      return;
    }
  }

  private _filterSurveyQuotaCriteria(quota: QuotaPayload): FilterQuotaKey {
    let isAidaAllowedQualificationCodeExisis = false;

    const _isValid =
      !!quota.criteria.length &&
      quota.criteria.every((criteria) => {
        const qualification =
          this._mappedDBQualificationData[criteria.qualification_code]!;

        if (!isAidaAllowedQualificationCodeExisis) {
          isAidaAllowedQualificationCodeExisis =
            this._aidaAllowedQualificationCodes.has(
              qualification?.dbQualificationCode
            );
        }

        if (
          this._invalidQualificationCodes.has(
            qualification?.dbQualificationCode
          )
        ) {
          isAidaAllowedQualificationCodeExisis = false;
          return false;
        }

        return !this._partialInvalidQualificationCodes.has(
          qualification?.dbQualificationCode
        );
      });

    if (isAidaAllowedQualificationCodeExisis && !_isValid) {
      return QUOTA_VALIDATE_TYPE.PARTIAL_INELIGIBLE;
    }
    return _isValid
      ? QUOTA_VALIDATE_TYPE.ELIGIBLE
      : QUOTA_VALIDATE_TYPE.INELIGIBLE;
  }

  private _checkForInvalidQuotas(quota: QuotaPayload) {
    const criteras = quota.criteria;
    if (!criteras.length) {
      return false;
    }
    const percentage: number =
      quota.percentage ?? (quota.required_count / this._completes) * 100;

    return criteras.forEach((criteria) => {
      const qualificationCode =
        this._mappedDBQualificationData[criteria.qualification_code]
          ?.dbQualificationCode;

      if (!this._aidaAllowedQualificationCodes.has(qualificationCode)) {
        this._invalidQualificationCodes.add(qualificationCode);
        return false;
      }

      if (
        this._partialInvalidQualificationCodes.has(qualificationCode) ||
        this._invalidQualificationCodes.has(qualificationCode)
      ) {
        return false;
      }

      const response = new AidaQuotaCriteriaValidaor(
        percentage,
        criteria,
        this._mappedDBQualificationData,
        this._countryCode
      ).valid();

      if (!response.valid && response.error) {
        this._errorMessages.push(response.error);
      }

      if (!response.valid) {
        this._partialInvalidQualificationCodes.add(qualificationCode);
        return false;
      }

      return response.valid;
    });
  }

  private _filterSelectionQuotas(): FilterQuota<AidaQbpSelectionQuota> {
    const response = this._selectionQuotas.reduce(
      (acc, quota) => {
        const groupKey = this._filterSelectionQualificationCodes(quota);
        acc[groupKey].push(quota);
        return acc;
      },
      {
        eligible: [],
        ineligible: [],
        partialIneligible: [],
        errorMessages: this._errorMessages,
      } as FilterQuota<AidaQbpSelectionQuota>
    );
    return response;
  }

  private _filterSelectionQualificationCodes(
    quota: AidaQbpSelectionQuota
  ): FilterQuotaKey {
    let isAidaAllowedQualificationCodeExisis = false;
    const _isValid =
      quota?.qualificationData &&
      Object.keys(quota?.qualificationData).length &&
      Object.values(quota?.qualificationData).every(
        ({ dbQualificationCode }: AidaMappedQualification) => {
          if (!isAidaAllowedQualificationCodeExisis) {
            isAidaAllowedQualificationCodeExisis =
              this._aidaAllowedQualificationCodes.has(dbQualificationCode);
          }

          if (this._invalidQualificationCodes.has(dbQualificationCode)) {
            isAidaAllowedQualificationCodeExisis = false;
            return false;
          }

          return !this._partialInvalidQualificationCodes.has(
            dbQualificationCode
          );
        }
      );

    if (isAidaAllowedQualificationCodeExisis && !_isValid) {
      return QUOTA_VALIDATE_TYPE.PARTIAL_INELIGIBLE;
    }

    return _isValid
      ? QUOTA_VALIDATE_TYPE.ELIGIBLE
      : QUOTA_VALIDATE_TYPE.INELIGIBLE;
  }
}
